import React, { useEffect, useState, useMemo } from 'react';
import Pagination from '../components/common/Pagination';
import usePurchaseOrder from '../hooks/usePurchaseOrder';
import PurchaseDaybookActions from '../components/purchase-daybook/PurchaseDaybookActions';
import PurchaseDaybookTable from '../components/purchase-daybook/PurchaseDaybookTable';
import MessageAlert from '../components/common/MessageAlert';
import { useWarehouseContext } from '../context/WarehouseContext';
import { printTableData } from '../utils/tablePrintHelper';
import purchaseOrderService from '../services/purchaseOrderService';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import SuperAdminDeleteDialog from '../components/common/SuperAdminDeleteDialog';
import { useCallback as reactUseCallback } from 'react';
import { formatBulkDeleteMessage } from '../utils/format';
import { useTranslation } from 'react-i18next';


const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'purchase_daybook_page_size';

const getDefaultDateRange = () => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    const todayStr = fmt(today);
    return {
        from: todayStr,
        to: todayStr,
    };
};

const PurchaseDaybookPage = () => {
    const { t } = useTranslation();
    const {
        purchaseOrders: daybookEntries,
        loading,
        error,
        fetchPurchaseDaybook,
        bulkDeletePurchaseOrders,
        exportPurchaseDaybook,
        clearError,
        totalPages,
        totalCount,
    } = usePurchaseOrder();
    const { userRole } = useRole();

    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState(getDefaultDateRange());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const { selectedWarehouseIds } = useWarehouseContext();
    const [alertMessage, setAlertMessage] = useState(null);
    const [printLoading, setPrintLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    useEffect(() => {
        const skip = (currentPage - 1) * pageSize;
        fetchPurchaseDaybook({
            from_date: dateRange.from,
            to_date: dateRange.to,
            skip,
            limit: pageSize,
            gst_included: false,
            sort_by: 'serial_number',
            sort_order: 'asc'
        }, { force: true });
    }, [fetchPurchaseDaybook, dateRange.from, dateRange.to, selectedWarehouseIds, currentPage, pageSize]);

    useEffect(() => {
        if (error) {
            setAlertMessage({ type: 'error', title: t('common.status.error'), message: error });
            clearError();
        }
    }, [error, clearError]);

    const handleRefresh = async () => {
        const skip = (currentPage - 1) * pageSize;
        await fetchPurchaseDaybook({
            from_date: dateRange.from,
            to_date: dateRange.to,
            skip,
            limit: pageSize,
            gst_included: false,
            sort_by: 'serial_number',
            sort_order: 'asc'
        }, { force: true });
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleFromDateChange = (val) => {
        setDateRange(prev => ({ ...prev, from: val }));
        setCurrentPage(1);
    };

    const handleSelectEntry = reactUseCallback((id, selected) => {
        const idStr = String(id);
        setSelectedEntries(prev => {
            if (selected) return [...prev, idStr];
            return prev.filter(entryId => String(entryId) !== idStr);
        });
    }, []);

    const handleSelectAll = reactUseCallback((selected, ids = []) => {
        if (selected) {
            setSelectedEntries(prev => {
                const newSelection = [...prev];
                ids.forEach(id => {
                    if (!newSelection.includes(id)) newSelection.push(id);
                });
                return newSelection;
            });
        } else {
            setSelectedEntries(prev => prev.filter(id => !ids.includes(id)));
        }
    }, []);

    const handleBulkDelete = async (password) => {
        const res = await bulkDeletePurchaseOrders(selectedEntries, password);
        if (res.success) {
            setSelectedEntries([]);
            setIsDeleteDialogOpen(false);
            setAlertMessage({
                type: 'success',
                title: t('common.status.success'),
                message: formatBulkDeleteMessage(res, 'Selected purchase orders deleted successfully')
            });
        } else {
            setAlertMessage({ type: 'error', title: t('common.status.error'), message: res.error || 'Failed to delete selected purchase orders' });
        }
    };

    const handleToDateChange = (val) => {
        setDateRange(prev => ({ ...prev, to: val }));
        setCurrentPage(1);
    };

    const handlePrint = async () => {
        setPrintLoading(true);
        try {
            const res = await purchaseOrderService.getPurchaseDaybook({
                from_date: dateRange.from,
                to_date: dateRange.to,
                limit: 500,
                skip: 0,
                gst_included: false,
                sort_by: 'serial_number',
                sort_order: 'asc'
            });
            const rawData = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.items || []) : [];

            // Sort data by Serial Number oldest first
            const data = [...rawData].sort((a, b) => {
                const aVal = a.serial_number || '';
                const bVal = b.serial_number || '';
                return aVal.localeCompare(bVal, undefined, { numeric: true });
            });
            const columns = [
                { id: 'date', label: 'Date' },
                { id: 'serial_number', label: t('commonCols.serialNumber') },
                { id: 'tally_name', label: t('commonCols.tallyName') },
                { id: 'vehicle_numbers', label: t('commonCols.truckNumber') },
                { id: 'delivery_location_name', label: 'Delivered To' },
                { id: 'order_for', label: t('commonCols.orderedBy') },
                { id: 'unloaded_by_name', label: t('commonCols.unloadedBy') },
                { id: 'labour_count', label: t('commonCols.labours') },
                { id: 'actual_quantity', label: 'Actual Quantity' },
                { id: 'total_amount', label: t('commonCols.amount') },
                { id: 'payment_terms', label: 'Bill Number' },
                { id: 'remark', label: t('commonCols.remarks') },
                {
                    id: 'edited',
                    label: t('commonCols.statusOptions.edited'),
                    getValue: (r) => (r.edited ? 'Yes' : 'No'),
                },
            ];

            printTableData(data, columns, `${t('purchaseDaybook.page.header.title')} (${dateRange.from} to ${dateRange.to})`);
        } catch (err) {
            setAlertMessage({ type: 'error', title: t('common.status.error'), message: 'Failed to load data for printing.' });
        } finally {
            setPrintLoading(false);
        }
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const res = await exportPurchaseDaybook({
                from_date: dateRange.from,
                to_date: dateRange.to,
                gst_included: false,
                ...(searchTerm && { search: searchTerm }),
            });
            if (!res.success) {
                setAlertMessage({ type: 'error', title: 'Export Failed', message: res.error });
            }
        } catch (err) {
            setAlertMessage({ type: 'error', title: 'Export Error', message: 'An unexpected error occurred during export.' });
        } finally {
            setExportLoading(false);
        }
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm.trim()) return daybookEntries;
        const t = searchTerm.toLowerCase().trim();
        return (daybookEntries || []).filter(entry => {
            return (
                (entry.po_number || '').toLowerCase().includes(t) ||
                (entry.tally_name || '').toLowerCase().includes(t) ||
                (entry.vehicle_numbers || '').toLowerCase().includes(t) ||
                (entry.delivery_location_name || '').toLowerCase().includes(t) ||
                (entry.order_for || '').toLowerCase().includes(t) ||
                (entry.unloaded_by_name || '').toLowerCase().includes(t) ||
                (entry.remark || '').toLowerCase().includes(t) ||
                (entry.status || '').toLowerCase().includes(t)
            );
        });
    }, [daybookEntries, searchTerm]);

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
            {alertMessage && (
                <div className="fixed top-4 right-4 z-50">
                    <MessageAlert
                        type={alertMessage.type}
                        title={alertMessage.title}
                        message={alertMessage.message}
                        onClose={() => setAlertMessage(null)}
                    />
                </div>
            )}

            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('purchaseDaybook.page.header.title')}</h1>
                        <p className="mt-2 text-gray-600">{t('purchaseDaybook.page.header.subtitle')}</p>
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <PurchaseDaybookActions
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onRefresh={handleRefresh}
                    onPrint={handlePrint}
                    printLoading={printLoading}
                    onExport={handleExport}
                    exportLoading={exportLoading}
                    fromDate={dateRange.from}
                    toDate={dateRange.to}
                    onFromDateChange={handleFromDateChange}
                    onToDateChange={handleToDateChange}
                    loading={loading}
                    canExport={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
                    selectedCount={selectedEntries.length}
                    onBulkDelete={() => setIsDeleteDialogOpen(true)}
                />
            </div>

            <PurchaseDaybookTable
                daybookEntries={filteredEntries}
                loading={loading}
                selectedEntries={selectedEntries}
                onSelectEntry={handleSelectEntry}
                onSelectAll={handleSelectAll}
            />

            <SuperAdminDeleteDialog
                isOpen={isDeleteDialogOpen}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleBulkDelete}
                loading={loading}
                title="Bulk Delete Purchase Orders"
                description={`Are you sure you want to delete ${selectedEntries.length} selected purchase orders? This action cannot be undone.`}
            />

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total records"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />
        </div>
    );
};

export default PurchaseDaybookPage;
